import type {
  ColumnFilter,
  FilterSpec,
  UiOperator,
} from "@tsmono/inspect-components/columnFilter";

import { FilterAst, parseFilter } from "./filterAst";
import { FilterVarKind, SampleFilterSpecRegistry } from "./filterSpecRegistry";

interface PredicateResult {
  colId: string;
  kind: FilterVarKind;
  spec: FilterSpec;
}

const REGEX_META = ".*+?^${}()|[]\\";

// Match the synthesizer's style: regex metachars are wrapped as `[X]`,
// except `^`, `]`, and `\` which it backslash-escapes (`[^]` matches ANY
// char and `[]]` never matches, so those class forms are NOT literals
// and must stay unrecognized).
const META_FOR_CLASS = ".*+?${}()|[";

/** Match the between shape the synthesizer emits — `(var >= a and var <= b)`
 *  on one variable (either comparison order). Recognized as a single leaf so
 *  a between inside an AND/OR condition pair doesn't flatten into three
 *  same-column predicates (unrepresentable) or break the pair recognizer
 *  (whose sides must be single predicates). */
const andAsBetween = (
  ast: FilterAst
): { varName: string; low: number; high: number } | null => {
  if (ast.kind !== "binary" || ast.op !== "and") return null;
  const bound = (node: FilterAst) =>
    node.kind === "binary" &&
    (node.op === ">=" || node.op === "<=") &&
    node.left.kind === "var" &&
    node.right.kind === "num"
      ? { op: node.op, name: node.left.name, value: node.right.value }
      : null;
  const a = bound(ast.left);
  const b = bound(ast.right);
  if (!a || !b || a.name !== b.name || a.op === b.op) return null;
  const low = a.op === ">=" ? a.value : b.value;
  const high = a.op === "<=" ? a.value : b.value;
  return { varName: a.name, low, high };
};

/** Walk top-level `and` nodes, collecting leaf predicates. A between-shaped
 *  `and` is a leaf, not a conjunction to descend into. */
const collectAndPredicates = (ast: FilterAst): FilterAst[] => {
  if (ast.kind === "binary" && ast.op === "and" && !andAsBetween(ast)) {
    return [
      ...collectAndPredicates(ast.left),
      ...collectAndPredicates(ast.right),
    ];
  }
  return [ast];
};

/** Try to interpret a `~=` regex literal as `^prefix`, `suffix$`, both, or
 *  plain `contains`. Returns `null` if the regex contains real metachars
 *  beyond the optional anchors. */
const parseRegexLiteral = (
  raw: string
): { anchor: "start" | "end" | "both" | "none"; literal: string } | null => {
  let s = raw;
  let anchorStart = false;
  let anchorEnd = false;
  if (s.startsWith("^")) {
    anchorStart = true;
    s = s.slice(1);
  }
  // Trailing `$` is an anchor unless escaped (`\$`). An even run of
  // backslashes before it is pairs of escaped backslashes (`foo\\$`
  // ends with a literal `\`), so the `$` is still a real anchor.
  if (s.endsWith("$")) {
    let backslashes = 0;
    for (let j = s.length - 2; j >= 0 && s[j] === "\\"; j--) backslashes++;
    if (backslashes % 2 === 0) {
      anchorEnd = true;
      s = s.slice(0, -1);
    }
  }

  let literal = "";
  let i = 0;
  while (i < s.length) {
    const ch = s[i] ?? "";
    const next = s[i + 1] ?? "";
    // `[X]` character-class shorthand for a literal regex metachar.
    if (
      ch === "[" &&
      i + 2 < s.length &&
      s[i + 2] === "]" &&
      META_FOR_CLASS.includes(next)
    ) {
      literal += next;
      i += 3;
      continue;
    }
    // `\X` for a literal metachar (the synthesizer emits `\\`, `\^`,
    // and `\]`; hand-typed escapes of other metachars are literal too).
    if (ch === "\\" && REGEX_META.includes(next)) {
      literal += next;
      i += 2;
      continue;
    }
    if (REGEX_META.includes(ch)) return null; // unescaped metachar
    literal += ch;
    i++;
  }

  if (anchorStart && anchorEnd) return { anchor: "both", literal };
  if (anchorStart) return { anchor: "start", literal };
  if (anchorEnd) return { anchor: "end", literal };
  return { anchor: "none", literal };
};

/** Walk past a leading `not` to surface the inner predicate plus the
 *  effective negation count (parity). */
const stripNot = (ast: FilterAst): { negated: boolean; inner: FilterAst } => {
  let negated = false;
  let inner = ast;
  while (inner.kind === "unary" && inner.op === "not") {
    negated = !negated;
    inner = inner.arg;
  }
  return { negated, inner };
};

const opposite: Partial<Record<UiOperator, UiOperator>> = {
  "=": "!=",
  "!=": "=",
  contains: "does not contain",
  "does not contain": "contains",
  "is blank": "is not blank",
  "is not blank": "is blank",
};

const colIdForContainsFn = (
  fn: string,
  registry: SampleFilterSpecRegistry
): string | null => {
  for (const [colId, mapping] of registry.byColId) {
    if (mapping.containsFn === fn) return colId;
  }
  return null;
};

const binaryToSpec = (
  ast: Extract<FilterAst, { kind: "binary" }>,
  registry: SampleFilterSpecRegistry
): PredicateResult | null => {
  // Only handle `var op literal` (or `var op None`).
  if (ast.left.kind !== "var") return null;
  const colId = registry.byVariable.get(ast.left.name);
  if (!colId) return null;
  const mapping = registry.byColId.get(colId);
  if (!mapping) return null;

  // var == None / var != None  ->  is blank / is not blank
  if (ast.right.kind === "const" && ast.right.name === "None") {
    if (ast.op === "==") {
      return {
        colId,
        kind: mapping.kind,
        spec: { operator: "is blank", value: "" },
      };
    }
    if (ast.op === "!=") {
      return {
        colId,
        kind: mapping.kind,
        spec: { operator: "is not blank", value: "" },
      };
    }
    return null;
  }

  // var ~= "regex"  ->  contains / starts with / ends with / =
  if (ast.op === "~=" && ast.right.kind === "str") {
    if (mapping.kind !== "string") return null;
    const parsed = parseRegexLiteral(ast.right.value);
    if (!parsed) return null;
    let operator: UiOperator;
    switch (parsed.anchor) {
      case "both":
        operator = "=";
        break;
      case "start":
        operator = "starts with";
        break;
      case "end":
        operator = "ends with";
        break;
      case "none":
        operator = "contains";
        break;
    }
    return {
      colId,
      kind: mapping.kind,
      spec: { operator, value: parsed.literal },
    };
  }

  // Numeric comparators
  if (mapping.kind === "number" && ast.right.kind === "num") {
    const opMap: Partial<Record<string, UiOperator>> = {
      "==": "=",
      "!=": "!=",
      "<": "<",
      "<=": "<=",
      ">": ">",
      ">=": ">=",
    };
    const operator = opMap[ast.op];
    if (!operator) return null;
    return {
      colId,
      kind: mapping.kind,
      spec: { operator, value: String(ast.right.value) },
    };
  }

  // String equals/notEqual
  if (mapping.kind === "string" && ast.right.kind === "str") {
    if (ast.op === "==") {
      return {
        colId,
        kind: mapping.kind,
        spec: { operator: "=", value: ast.right.value },
      };
    }
    if (ast.op === "!=") {
      return {
        colId,
        kind: mapping.kind,
        spec: { operator: "!=", value: ast.right.value },
      };
    }
  }

  return null;
};

/** The non-negated portion of `predicateToSpec`. */
const predicateToSpecInner = (
  ast: FilterAst,
  registry: SampleFilterSpecRegistry
): PredicateResult | null => {
  // 1. xxx_contains("literal") -> { contains, value: literal }
  if (ast.kind === "call" && ast.args.length === 1) {
    const fn = ast.fn;
    const arg = ast.args[0];
    if (!arg || arg.kind !== "str") return null;
    const colId = colIdForContainsFn(fn, registry);
    if (!colId) return null;
    const mapping = registry.byColId.get(colId);
    if (!mapping) return null;
    // The synthesizer regex-escapes the value before emitting; reverse
    // that here so the spec round-trips to the user's input. If the
    // literal isn't a plain regex-escaped string, skip.
    const parsed = parseRegexLiteral(arg.value);
    if (!parsed || parsed.anchor !== "none") return null;
    return {
      colId,
      kind: mapping.kind,
      spec: { operator: "contains", value: parsed.literal },
    };
  }

  // 2. LEFT or RIGHT -> same-column OR pair
  if (ast.kind === "binary" && ast.op === "or") {
    return orToPairSpec(ast, registry);
  }

  // 3. (var >= a and var <= b) -> between (the synthesizer's between form,
  //    surfaced as a leaf by collectAndPredicates and pair recognition)
  if (ast.kind === "binary" && ast.op === "and") {
    const between = andAsBetween(ast);
    if (!between) return null;
    const colId = registry.byVariable.get(between.varName);
    if (!colId) return null;
    const mapping = registry.byColId.get(colId);
    if (!mapping || mapping.kind !== "number") return null;
    return {
      colId,
      kind: mapping.kind,
      spec: {
        operator: "between",
        value: String(between.low),
        value2: String(between.high),
      },
    };
  }

  // 4. var BINARY_OP literal
  if (ast.kind === "binary") {
    return binaryToSpec(ast, registry);
  }

  return null;
};

/** Map a leaf predicate to a single-column spec. Returns null when the
 *  predicate isn't round-trippable. */
const predicateToSpec = (
  ast: FilterAst,
  registry: SampleFilterSpecRegistry
): PredicateResult | null => {
  const { negated, inner } = stripNot(ast);
  const result = predicateToSpecInner(inner, registry);
  if (!result) return null;
  if (!negated) return result;
  // A negated pair (`not (a or b)`) can't be flipped into a single
  // condition — stays expression-only (plan decision 7).
  if (result.spec.join) return null;
  const flipped = opposite[result.spec.operator];
  if (!flipped) return null; // can't negate this operator
  return {
    ...result,
    spec: { ...result.spec, operator: flipped },
  };
};

/** Recognize a same-column OR leaf (`a or b`) as a two-condition pair.
 *  Each side is resolved via the normal single-predicate path (including
 *  negation); both sides must land on the same column as single
 *  conditions (not already a pair) or this returns null. */
const orToPairSpec = (
  ast: Extract<FilterAst, { kind: "binary" }>,
  registry: SampleFilterSpecRegistry
): PredicateResult | null => {
  const left = predicateToSpec(ast.left, registry);
  const right = predicateToSpec(ast.right, registry);
  if (!left || !right) return null;
  if (left.colId !== right.colId) return null; // cross-column OR stays expression-only
  if (left.spec.join || right.spec.join) return null; // no nested pairs
  return {
    colId: left.colId,
    kind: left.kind,
    spec: { ...left.spec, join: "or", second: right.spec },
  };
};

/** Detect the between pattern: `>= a` AND `<= b` on the same numeric
 *  column. Returns the equivalent single spec or null. */
const tryBetween = (specs: FilterSpec[]): FilterSpec | null => {
  if (specs.length !== 2) return null;
  const [a, b] = specs;
  if (!a || !b) return null;
  let lower: string | undefined;
  let upper: string | undefined;
  for (const s of [a, b]) {
    if (s.operator === ">=") lower = s.value;
    else if (s.operator === "<=") upper = s.value;
  }
  if (lower === undefined || upper === undefined) return null;
  return { operator: "between", value: lower, value2: upper };
};

/**
 * Try to express the AST as per-column filter specs. The recognized
 * subset is a top-level conjunction of column predicates — anything
 * else (`or`, arithmetic, function calls outside `xxx_contains`,
 * unsupported operators) returns `null`, signaling to the caller that
 * the text is "expression-only".
 */
export function astToSpecs(
  ast: FilterAst,
  registry: SampleFilterSpecRegistry
): Record<string, ColumnFilter> | null {
  const predicates = collectAndPredicates(ast);

  const perColumn = new Map<
    string,
    { kind: FilterVarKind; specs: FilterSpec[] }
  >();

  for (const pred of predicates) {
    const result = predicateToSpec(pred, registry);
    if (!result) return null;
    let column = perColumn.get(result.colId);
    if (!column) {
      column = { kind: result.kind, specs: [] };
      perColumn.set(result.colId, column);
    }
    if (column.kind !== result.kind) return null;
    column.specs.push(result.spec);
  }

  const specs: Record<string, ColumnFilter> = {};
  for (const [colId, column] of perColumn) {
    const filterType = column.kind === "number" ? "number" : "string";
    if (column.specs.length === 1) {
      const [spec] = column.specs;
      if (!spec) return null;
      specs[colId] = { columnId: colId, filterType, spec };
    } else if (column.specs.length === 2) {
      // An OR-pair leaf already holds 2 conditions by itself; a second
      // predicate alongside it on the same column can't be folded in
      // (plan decision: OR-pair + anything else on that column -> null).
      if (column.specs.some((s) => s.join)) return null;
      // The >=/<= pair folds to `between` (fold wins, keeps round-trip
      // stability); any other pair of predicates becomes an AND pair.
      const between = tryBetween(column.specs);
      if (between) {
        specs[colId] = { columnId: colId, filterType, spec: between };
      } else {
        const [first, second] = column.specs;
        if (!first || !second) return null;
        specs[colId] = {
          columnId: colId,
          filterType,
          spec: { ...first, join: "and", second },
        };
      }
    } else {
      // ≥3 predicates on one column can't be represented in the popover.
      return null;
    }
  }

  return specs;
}

/** Parse + recognize in one step. `""`/whitespace → `{}`. Parse error or
 *  unrepresentable expression → null. */
export function parseFilterSpecs(
  text: string,
  registry: SampleFilterSpecRegistry
): Record<string, ColumnFilter> | null {
  if (text.trim() === "") return {};
  const { ast } = parseFilter(text);
  if (!ast) return null;
  return astToSpecs(ast, registry);
}
